import { toNumber } from "../format.ts";
import type { GatewayBrowserClient } from "../gateway.ts";
import type { SessionsListResult } from "../types.ts";

export type SessionsDeleteConfirm = {
  key: string;
};

export type SessionsDeleteAllConfirm = {
  /**
   * Session keys the modal will display and confirm for deletion. The
   * main session is excluded here — `requestSessionDeleteAll` filters
   * it out before storing so the modal doesn't need to gate on it.
   */
  keys: string[];
  /**
   * Per-row catalog entries for the keys above, in catalog order. The
   * modal renders these directly so the title + metadata line up with
   * the sidebar (UX-R-confirm-list, JAISIU-3327). Stamped at request
   * time so `selectedCount` and `rows.length` are always in sync.
   */
  displayRows: import("../types.ts").GatewaySessionRow[];
};

/**
 * The narrow host slice the *request* half of the bulk-delete flow
 * touches.
 *
 * JAISIU-types-sweep: `requestSessionDeleteAll` /
 * `cancelSessionDeleteAll` were typed against the whole `SessionsState`
 * (≈15 required fields) even though they only read `client` /
 * `connected` and write `sessionsDeleteAllConfirm`. The chat-history
 * bulk-select controller (`chat-history-bulk-select.ts`) drives them
 * from a narrower host and had to be rejected by the compiler.
 *
 * `client` is declared at the request-method width rather than
 * `GatewayBrowserClient | null` because that is the only member the
 * bulk-select host actually implements (`BulkSelectClient`), and it
 * still accepts the real `GatewayBrowserClient`. `sessionsDeleteAllConfirm`
 * is optional + nullable because `SessionsState` declares it that way
 * and `confirmSessionDeleteAll` reads it defensively (`if (!confirm)`).
 */
export type SessionDeleteAllRequestState = {
  client: { request: (method: string, params: unknown) => Promise<unknown> } | null;
  connected: boolean;
  sessionsDeleteAllConfirm?: SessionsDeleteAllConfirm | null;
};

export type SessionsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionsLoading: boolean;
  /**
   * In-flight quiet refresh mutex. Does not drive loading UI — used by chat
   * polling so the sidebar can keep showing the previous list (no flicker).
   */
  sessionsPolling?: boolean;
  /**
   * True once a `loadSessions` call has actually run to completion — whether
   * it returned rows or threw.
   *
   * The sidebar's "No chats yet" branch is driven by `sessionsResult === null`
   * combined with a loading flag, and the chat-history catalog path is hard
   * coded to `quiet: true`, which only ever touches `sessionsPolling` and
   * leaves `sessionsLoading` false. The loading flag was therefore
   * permanently false on the chat tab, so the empty branch rendered over a
   * catalog that was still in flight — and, because a failed fetch leaves
   * `sessionsResult` null, keying loading off the result alone would instead
   * spin "Loading…" forever. This flag settles on BOTH outcomes.
   */
  sessionsCatalogSettled?: boolean;
  sessionsResult: SessionsListResult | null;
  /**
   * The agentId that produced the current `sessionsResult`, or `null` when
   * the result is unfiltered. Stamped by `loadSessions` for callers that
   * need to know whether the listed rows match the active agent (e.g. the
   * sessions hub for "show only this agent's sessions" toggles).
   *
   * As of 2026-09-07 the chat-history sidebar does NOT filter by agentId —
   * it shows every session the operator can see. The catalog consumer
   * (`buildChatHistoryGroups`) no longer reads this stamp for filtering,
   * so a mismatch here will not produce a "full list → empty" flicker
   * anymore (JAISIU-3121).
   */
  /**
   * JAISIU-types-sweep: optional. This stamp is written by `loadSessions`
   * and read by nobody outside the controller, so an absent value is
   * exactly "no stamp yet" — `loadSessions` assigns
   * `overrides?.agentId?.trim() ?? null` unconditionally, so the
   * production host (`app.ts`, `= null`) always has it after the first
   * load. Optional also lets test hosts spread a
   * `Partial<SessionsState>` without the value degrading to
   * `string | null | undefined`.
   */
  sessionsResultAgentId?: string | null;
  sessionsError: string | null;
  sessionsFilterActive: string;
  sessionsFilterLimit: string;
  sessionsIncludeGlobal: boolean;
  sessionsIncludeUnknown: boolean;
  sessionsSearchQuery: string;
  sessionsDeleteConfirm?: SessionsDeleteConfirm | null;
  sessionsDeleteAllConfirm?: SessionsDeleteAllConfirm | null;
};

type LoadSessionsOptions = {
  /** When true, reload even if a sessions request is already in flight (e.g. after delete). */
  force?: boolean;
  /**
   * Background refresh: skip the `sessionsLoading` UI flag and suppress error
   * toasts so poll ticks do not wipe the chat sidebar to "Loading chats…".
   */
  quiet?: boolean;
  /**
   * JAISIU-3121: when true and the caller did not pass an explicit `agentId`,
   * reuse `state.sessionsResultAgentId` instead of dropping the filter.
   * Use this for any sidebar-touching background refresh — poll ticks,
   * run-final refreshes, the refresh button, fork, reset. Without it,
   * every quiet tick flips `sessionsResult` from "agent-scoped" back to
   * "all agents", and the catalog re-filters the wider set against the
   * narrow agentId → the visible list shrinks / flickers.
   */
  sticky?: boolean;
};

export async function loadSessions(
  state: SessionsState,
  overrides?: {
    activeMinutes?: number;
    limit?: number;
    includeGlobal?: boolean;
    includeUnknown?: boolean;
    includeLastMessage?: boolean;
    includeDerivedTitles?: boolean;
    agentId?: string;
    search?: string;
  },
  opts?: LoadSessionsOptions & {
    /**
     * Ordered list of agentIds to retry after the primary `agentId` returns
     * zero displayable sessions. Stops as soon as a non-empty result lands.
     * Used by the chat-history sidebar to fall back to "main" when the
     * active session is a subagent whose owning agent has no chat history
     * the operator can navigate to.
     */
    agentIdFallbacks?: string[];
  },
) {
  if (!state.client || !state.connected) {
    return;
  }
  const quiet = opts?.quiet === true;
  if (!opts?.force) {
    if (state.sessionsLoading || state.sessionsPolling) {
      return;
    }
  }
  if (quiet) {
    state.sessionsPolling = true;
  } else {
    state.sessionsLoading = true;
    state.sessionsError = null;
  }
  const fallbacks = Array.isArray(opts?.agentIdFallbacks) ? opts!.agentIdFallbacks : [];
  const triedAgentIds = new Set<string>();
  try {
    const runOnce = async (agentIdOverride: string | undefined) => {
      const includeGlobal = overrides?.includeGlobal ?? state.sessionsIncludeGlobal;
      const includeUnknown = overrides?.includeUnknown ?? state.sessionsIncludeUnknown;
      const activeMinutes = overrides?.activeMinutes ?? toNumber(state.sessionsFilterActive, 0);
      const limit = overrides?.limit ?? toNumber(state.sessionsFilterLimit, 0);
      const search = overrides?.search ?? state.sessionsSearchQuery;
      const params: Record<string, unknown> = {
        includeGlobal,
        includeUnknown,
      };
      if (activeMinutes > 0) {
        params.activeMinutes = activeMinutes;
      }
      if (limit > 0) {
        params.limit = limit;
      }
      if (overrides?.includeLastMessage === true) {
        params.includeLastMessage = true;
      }
      if (overrides?.includeDerivedTitles === true) {
        params.includeDerivedTitles = true;
      }
      const trimmed = agentIdOverride?.trim();
      if (trimmed && !triedAgentIds.has(trimmed)) {
        params.agentId = trimmed;
        triedAgentIds.add(trimmed);
      }
      if (search.trim()) {
        params.search = search.trim();
      }
      return await state.client!.request<SessionsListResult | undefined>("sessions.list", params);
    };

    const primaryRes = await runOnce(overrides?.agentId);
    let result = primaryRes;
    if (result) {
      state.sessionsResult = result;
      state.sessionsResultAgentId = overrides?.agentId?.trim() ?? null;
    }
    // JAISIU-3109: if the primary agentId call returned nothing the sidebar
    // can show, walk the operator-supplied fallback list (typically "main")
    // exactly once. The repo's filtered result is replaced wholesale — this
    // preserves the contract that `state.sessionsResult` reflects the
    // current view's source-of-truth.
    if (result && fallbacks.length > 0 && (result.sessions?.length ?? 0) === 0) {
      for (const fallback of fallbacks) {
        const trimmed = fallback?.trim();
        if (!trimmed || triedAgentIds.has(trimmed)) {
          continue;
        }
        const fallbackRes = await runOnce(trimmed);
        if (fallbackRes?.sessions && fallbackRes.sessions.length > 0) {
          state.sessionsResult = fallbackRes;
          state.sessionsResultAgentId = trimmed ?? null;
          result = fallbackRes;
          break;
        }
        if (fallbackRes) {
          result = fallbackRes;
        }
      }
    }
  } catch (err) {
    if (!quiet) {
      state.sessionsError = String(err);
    }
  } finally {
    if (quiet) {
      state.sessionsPolling = false;
      // Settle on success AND failure so the sidebar's loading placeholder
      // always resolves. Reached only when a fetch actually ran — the
      // in-flight early return above is outside the try block.
      state.sessionsCatalogSettled = true;
    } else {
      state.sessionsLoading = false;
      state.sessionsCatalogSettled = true;
    }
  }
}

export async function patchSession(
  state: SessionsState,
  key: string,
  patch: {
    label?: string | null;
    thinkingLevel?: string | null;
    verboseLevel?: string | null;
    reasoningLevel?: string | null;
    model?: string | null;
    sendPolicy?: "allow" | "deny" | null;
  },
) {
  if (!state.client || !state.connected) {
    return;
  }
  const params: Record<string, unknown> = { key };
  if ("label" in patch) {
    params.label = patch.label;
  }
  if ("thinkingLevel" in patch) {
    params.thinkingLevel = patch.thinkingLevel;
  }
  if ("verboseLevel" in patch) {
    params.verboseLevel = patch.verboseLevel;
  }
  if ("reasoningLevel" in patch) {
    params.reasoningLevel = patch.reasoningLevel;
  }
  if ("model" in patch) {
    params.model = patch.model;
  }
  if ("sendPolicy" in patch) {
    params.sendPolicy = patch.sendPolicy;
  }
  try {
    await state.client.request("sessions.patch", params);
    await loadSessions(state);
  } catch (err) {
    state.sessionsError = String(err);
  }
}

export type SessionsForkResult = {
  sessionKey: string;
  sessionId: string;
  label?: string;
};

export async function forkSession(
  state: SessionsState,
  sourceKey: string,
  label?: string,
  options?: { copyTranscript?: boolean },
): Promise<SessionsForkResult | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const key = sourceKey.trim();
  if (!key) {
    return null;
  }
  try {
    const res = await state.client.request<{
      ok?: boolean;
      sessionKey?: string;
      sessionId?: string;
      label?: string;
    }>("sessions.fork", {
      key,
      ...(label?.trim() ? { label: label.trim() } : {}),
      ...(options?.copyTranscript === false ? { copyTranscript: false } : {}),
    });
    const sessionKey = res?.sessionKey?.trim();
    const sessionId = res?.sessionId?.trim();
    if (!sessionKey || !sessionId) {
      state.sessionsError = "sessions.fork returned no session key";
      return null;
    }
    await loadSessions(state);
    return {
      sessionKey,
      sessionId,
      label: res?.label,
    };
  } catch (err) {
    state.sessionsError = String(err);
    return null;
  }
}

/** Open themed delete confirmation for a session key. */
export function requestSessionDelete(state: SessionsState, key: string): void {
  const trimmed = key.trim();
  if (!trimmed || !state.client || !state.connected) {
    return;
  }
  state.sessionsDeleteConfirm = { key: trimmed };
}

export function cancelSessionDelete(state: SessionsState): void {
  state.sessionsDeleteConfirm = null;
}

/** @returns true when the session was deleted on the gateway */
export async function confirmSessionDelete(state: SessionsState): Promise<boolean> {
  const key = state.sessionsDeleteConfirm?.key?.trim();
  if (!key) {
    return false;
  }
  if (!state.client || !state.connected) {
    return false;
  }
  state.sessionsDeleteConfirm = null;
  state.sessionsLoading = true;
  state.sessionsError = null;
  try {
    await state.client.request("sessions.delete", { key, deleteTranscript: true });
    await loadSessions(state, undefined, { force: true });
    return true;
  } catch (err) {
    state.sessionsError = String(err);
    return false;
  } finally {
    state.sessionsLoading = false;
  }
}

/**
 * Open themed delete-all confirmation for a list of session keys.
 *
 * `rows` MUST be the full sessions catalog (or at least contain the
 * row marked `systemSent=true`) so the main-session lookup resolves to
 * the real main, not whichever row happens to be first in the
 * operator's selection. The sidebar's `handleBulkDeleteSelectedChatSessions`
 * passes `sessionsResult.sessions` for this reason.
 *
 * The confirm modal shows EXACTLY the chats the operator selected —
 * minus the protected main session. We filter `rows` down to that set
 * so the modal's list doesn't include chats the user didn't ask to
 * delete (UX-R-confirm-list, JAISIU-3327).
 */
export function requestSessionDeleteAll(
  state: SessionDeleteAllRequestState,
  keys: string[],
  rows: import("../types.ts").GatewaySessionRow[],
): void {
  if (!keys.length || !state.client || !state.connected) {
    return;
  }
  // Determine main session: the row with systemSent=true (or first if none found).
  const mainKey = rows.find((r) => r.systemSent)?.key ?? rows[0]?.key ?? null;
  // Strip the main session from the keys the modal will display/delete.
  // If the operator's selection IS the main session, the modal will
  // show "Delete 0 chats?" with a clear empty list — that's correct
  // because there's nothing for the destructive action to remove.
  const deletableKeys = keys.filter((k) => k !== mainKey);
  // Build the per-row list the modal will render: every selected row,
  // in catalog order, that survives the main-session filter.
  const deletableSet = new Set(deletableKeys);
  const displayRows = rows.filter((r) => deletableSet.has(r.key));
  state.sessionsDeleteAllConfirm = {
    keys: deletableKeys,
    // Stamped here so the modal's "selectedCount" matches the row count
    // it actually renders — and so callers don't have to re-derive it
    // from `keys.length` (which would silently disagree if the rows
    // catalog is stale).
    displayRows,
  };
}

export function cancelSessionDeleteAll(state: SessionDeleteAllRequestState): void {
  state.sessionsDeleteAllConfirm = null;
}

/**
 * @returns the set of session keys that the gateway confirmed deleted
 * (i.e. the calls that didn't throw). The single-row delete path
 * returns a boolean; bulk returns the whole set so the parent can
 * decide what UI side-effects to run (e.g. switch active chat if the
 * operator deleted the one they had open — UX-R-bulk-active-delete).
 */
export async function confirmSessionDeleteAll(state: SessionsState): Promise<readonly string[]> {
  const confirm = state.sessionsDeleteAllConfirm;
  if (!confirm) {
    return [];
  }
  // The modal is a focused confirm — whatever the operator selected in
  // the sidebar (minus the main session, filtered at request time) is
  // what gets deleted.
  const keys = [...confirm.keys];
  if (!keys.length) {
    state.sessionsDeleteAllConfirm = null;
    return [];
  }
  if (!state.client || !state.connected) {
    state.sessionsDeleteAllConfirm = null;
    return [];
  }
  state.sessionsDeleteAllConfirm = null;
  state.sessionsLoading = true;
  state.sessionsError = null;

  const deletedKeys: string[] = [];
  try {
    for (const key of keys) {
      const trimmed = key.trim();
      if (!trimmed) continue;
      try {
        await state.client.request("sessions.delete", { key: trimmed, deleteTranscript: true });
        deletedKeys.push(trimmed);
      } catch {
        // Skip failures (e.g. main session protection) and continue
      }
    }
    await loadSessions(state, undefined, { force: true });
    return deletedKeys;
  } catch (err) {
    state.sessionsError = String(err);
    return deletedKeys;
  } finally {
    state.sessionsLoading = false;
  }
}

export type SessionHistoryState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionHistoryPanelKey: string | null;
  sessionHistoryMessages: unknown[];
  sessionHistoryLoading: boolean;
  sessionHistoryError: string | null;
};

export async function loadSessionHistory(
  state: SessionHistoryState,
  sessionKey: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    state.sessionHistoryError = "Not connected to gateway";
    return;
  }

  const key = sessionKey.trim();
  if (!key) {
    state.sessionHistoryError = "Session key is required";
    return;
  }

  state.sessionHistoryPanelKey = key;
  state.sessionHistoryLoading = true;
  state.sessionHistoryError = null;

  try {
    const res = await state.client.request<{
      messages?: unknown[];
      error?: string;
    }>("chat.history", {
      sessionKey: key,
      limit: 200,
    });

    if (res.error) {
      state.sessionHistoryError = String(res.error);
      state.sessionHistoryMessages = [];
    } else {
      state.sessionHistoryMessages = Array.isArray(res.messages) ? res.messages : [];
    }
  } catch (err) {
    state.sessionHistoryError = String(err);
    state.sessionHistoryMessages = [];
  } finally {
    state.sessionHistoryLoading = false;
  }
}

export function closeSessionHistory(state: SessionHistoryState): void {
  state.sessionHistoryPanelKey = null;
  state.sessionHistoryMessages = [];
  state.sessionHistoryLoading = false;
  state.sessionHistoryError = null;
}
